import styled from 'styled-components';

import { ProductFlow } from 'data/__generated__';

import { BundleTypeTag } from 'pages/Bundles/components/TypeTag';

import { BundleRow } from '../constants/bundlesColumns';

type BundlesSummaryHeaderProps = {
    bundles: BundleRow[];
    selectedIds: string[];
    onSelectionChange: (ids: string[]) => void;
};

type FlowStats = {
    bundles: number;
    boxes: number;
    deliveries: number;
    products: number;
    references: number;
};

const computeFlowStats = (flowBundles: BundleRow[]): FlowStats => {
    const deliveryIds = new Set<string>();
    // References must be deduplicated across the whole flow: bundle.numberOfReferences is already
    // distinct *within* a bundle, so summing it double-counts a reference shared by several bundles.
    const referenceIds = new Set<string>();
    let boxes = 0;
    let products = 0;

    flowBundles.forEach((bundle) => {
        products += bundle.numberOfUnits;
        bundle.boxes.forEach((box) => {
            boxes += 1;
            deliveryIds.add(box.deliveryId);
            box.products.forEach((product) => referenceIds.add(product._id));
        });
    });

    return {
        bundles: flowBundles.length,
        boxes,
        deliveries: deliveryIds.size,
        products,
        references: referenceIds.size,
    };
};

/**
 * Le bilan de la préparation affichée, flux par flux, et le raccourci pour sélectionner tout un flux.
 *
 * Il porte sur les bundles AFFICHÉS, pas sur le jour courant : le périmètre est déjà celui de la chip
 * « Date de préparation », et un bilan calé sur « aujourd'hui » disparaissait purement et simplement
 * dès qu'aucune préparation n'était prévue le jour même (un week-end, ou une date élargie à la main).
 */
export const BundlesSummaryHeader = ({ bundles, selectedIds, onSelectionChange }: BundlesSummaryHeaderProps) => {
    const flowsPresent = Object.values(ProductFlow).filter((flow) => bundles.some(({ type }) => type === flow));

    if (flowsPresent.length === 0) return null;

    const selectedIdSet = new Set(selectedIds);

    const toggleFlow = (flow: ProductFlow) => {
        const flowIds = bundles.filter(({ type }) => type === flow).map(({ _id }) => _id);
        const allSelected = flowIds.every((id) => selectedIdSet.has(id));
        onSelectionChange(
            allSelected
                ? selectedIds.filter((id) => !flowIds.includes(id))
                : [...new Set([...selectedIds, ...flowIds])],
        );
    };

    return (
        <Container>
            {flowsPresent.map((flow) => {
                const flowBundles = bundles.filter(({ type }) => type === flow);
                const isSelected = flowBundles.every(({ _id }) => selectedIdSet.has(_id));
                const stats = computeFlowStats(flowBundles);
                return (
                    <FlowButton key={flow} type="button" $isSelected={isSelected} onClick={() => toggleFlow(flow)}>
                        <BundleTypeTag type={flow} />
                        <Stats>
                            <Stat>
                                <StatValue>{stats.bundles}</StatValue>
                                <StatLabel>bundles</StatLabel>
                            </Stat>
                            <Stat>
                                <StatValue>{stats.boxes}</StatValue>
                                <StatLabel>caisses</StatLabel>
                            </Stat>
                            <Stat>
                                <StatValue>{stats.deliveries}</StatValue>
                                <StatLabel>livraisons</StatLabel>
                            </Stat>
                            <Stat>
                                <StatValue>{stats.products}</StatValue>
                                <StatLabel>produits</StatLabel>
                            </Stat>
                            <Stat>
                                <StatValue>{stats.references}</StatValue>
                                <StatLabel>réfs</StatLabel>
                            </Stat>
                        </Stats>
                    </FlowButton>
                );
            })}
        </Container>
    );
};

/**
 * Un bandeau de la `DataView`, entre les filtres et la vue : il en épouse la largeur et se sépare de la
 * vue par un trait, plutôt que de flotter en carte.
 *
 * Une seule ligne, quitte à défiler : à s'enrouler, il grandirait avec le nombre de flux et mangerait
 * la hauteur du kanban juste en dessous.
 */
const Container = styled.div`
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    // La marge latérale, et non une gouttière interne : c'est la BOÎTE qui défile qui est en retrait,
    // donc les tuiles s'effacent sur du blanc de part et d'autre au lieu de filer jusqu'au bord. Le
    // retrait vaut celui des chips de filtre juste au-dessus, les deux rangées s'alignent.
    margin: 0 16px;
    padding: 10px 0;
    overflow-x: auto;
    border-bottom: 1px solid ${({ theme }) => theme.lightBorderColor};
`;

const FlowButton = styled.button<{ $isSelected: boolean }>`
    display: flex;
    flex-shrink: 0;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    padding: 8px 12px;
    border-radius: var(--radius-md);
    background-color: ${({ theme }) => theme.backgroundColor};
    border: 2px solid ${({ theme, $isSelected }) => ($isSelected ? theme.textColor : 'transparent')};
    transition: border-color 0.15s ease;

    &:hover {
        opacity: 0.85;
    }
`;

const Stats = styled.div`
    display: flex;
    align-items: stretch;
    gap: 10px;
`;

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 48px;

    &:not(:last-child) {
        border-right: 1px solid ${({ theme }) => theme.lightBorderColor};
        padding-right: 10px;
    }
`;

const StatValue = styled.span`
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.textColor};
`;

const StatLabel = styled.span`
    font-size: 11px;
    color: ${({ theme }) => theme.infoTextColor};
`;
